import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { NIVILABS_LOGO , LOGIN_IMG} from "@/lib/api";
import { api } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (forgot) {
        await api.post("/auth/forgot-password", { email });
        toast.success("If that email exists, a reset link was sent.");
        setForgot(false);
      } else {
        const u = await login(email, password);
        toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
        nav(u.role === "admin" ? "/admin" : "/");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sign in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-200px)] grid lg:grid-cols-2" data-testid="login-page">
      <div className="relative hidden lg:block">
        <img src={LOGIN_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/80 via-primary/40 to-secondary/40" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <img src={NIVILABS_LOGO} alt="nivilabs" className="h-16 w-16 object-contain" />
          <div>
            <div className="font-display text-4xl font-bold leading-tight">Welcome to nivilabs.</div>
            <p className="text-white/80 mt-3 max-w-md">Premium reagents, kits and consumables for life-science research — delivered with the trust of Bionivid Technology.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-5">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-primary font-semibold">{forgot ? "Reset password" : "Sign in"}</div>
            <h1 className="font-display text-3xl font-bold mt-1">{forgot ? "Forgot your password?" : "Welcome back"}</h1>
            <p className="text-sm text-muted-foreground mt-2">{forgot ? "Enter your email to receive reset instructions." : "Sign in to manage inquiries and saved products."}</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
          </div>
          {!forgot && (
            <div className="space-y-1.5">
              <Label htmlFor="pw">Password</Label>
              <div className="relative">
                <Input id="pw" type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="pr-10" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          )}
          <Button type="submit" disabled={loading} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="login-submit">
            {loading ? "Please wait…" : forgot ? "Send reset email" : "Sign in"}
          </Button>
          <div className="flex items-center justify-between text-sm">
            <button type="button" onClick={() => setForgot(!forgot)} className="text-primary hover:underline" data-testid="forgot-toggle">
              {forgot ? "Back to sign in" : ""}
            </button>
            <Link to="/register" className="text-muted-foreground hover:text-primary">Create account</Link>
          </div>
        </form>
      </div>
    </main>
  );
}
